/**
 * Partial edits of an existing event.
 *
 * `updateEvent` is not a patch endpoint: the PUT behind it replaces the event row
 * outright, it diffs `participants` against what the service already holds, and it
 * reads `sub_events` as `?? []` — so a payload that omits a field does not leave
 * that field alone, it clears it. Anything that means to change one thing has to
 * hand back everything else exactly as it stands, and this is the one place that
 * knows which fields those are.
 */
import type { CreateEventPayload } from '@/components/CreateSheet/EditEventSheet/EditEventSheet';
import { parseStops, STOP_SEP } from '@/utils/tripStops.ts';

import {
  type EventParticipant,
  EventParticipantType,
  type EventResponse,
  type SubEventInput,
  UNSPECIFIED_LOCATION,
} from './events.dto.ts';

/** An event's sub-activities as sub-event input, ids intact so they update rather than get recreated. */
export const echoSubEvents = (event: EventResponse): SubEventInput[] =>
  (event.sub_activities ?? []).map((sa) => ({
    id: sa.id,
    title: sa.title,
    description: sa.description,
    time_slot: { start: sa.time_slot.start, end: sa.time_slot.end },
    participants: sa.participants ?? [],
  }));

/** A payload that changes only what `override` names. */
export const buildEventPatch = (
  event: EventResponse,
  override: Partial<CreateEventPayload> = {},
): CreateEventPayload => ({
  id: event.id,
  parent_id: event.parent?.id,
  title: event.title,
  description: event.description ?? '',
  location: event.location,
  type: event.type,
  time_slot: event.time_slot
    ? {
        type: event.time_slot.type ?? 'day_based',
        start: event.time_slot.start,
        end: event.time_slot.end,
        all_day: event.time_slot.all_day,
      }
    : undefined,
  participants: event.participants ?? [],
  sub_events: echoSubEvents(event),
  ...override,
});

/**
 * The participant list the Audience step saves. It manages the crew it lists
 * (`crewIds`): those it leaves out are taken off, those it adds join. Guests and
 * crew it does not list (no longer aboard) stay as they are, since a participant
 * list that omits someone is a list that removes them.
 */
export const withCrewSelection = (
  event: EventResponse,
  crewIds: string[],
  participants: EventParticipant[],
): EventParticipant[] => {
  const kept = (event.participants ?? []).filter(
    (p) =>
      p.type === EventParticipantType.GUEST ||
      !crewIds.includes(p.person_id) ||
      participants.some((np) => np.person_id === p.person_id),
  );
  return [...kept, ...participants.filter((p) => !kept.some((k) => k.person_id === p.person_id))];
};

/** The location the Location step writes: the picker's stops, coordinates preserved. */
export const packLocation = (
  event: EventResponse,
  stops: string[],
): CreateEventPayload['location'] => ({
  ...event.location,
  name: stops[0] ?? UNSPECIFIED_LOCATION,
  address: stops.join(STOP_SEP),
});

/** Prefill for editing an event in the wizard — the shape its seed already takes. */
export const eventToSeed = (event: EventResponse) => ({
  title: event.title ?? '',
  description: event.description ?? '',
  locations: parseStops(event.location?.address, event.location?.name).filter(
    (name) => name !== UNSPECIFIED_LOCATION,
  ),
  startDate: event.time_slot ? new Date(event.time_slot.start) : null,
  endDate: event.time_slot ? new Date(event.time_slot.end) : null,
  allDay: event.time_slot?.all_day ?? false,
});
